import React, { useState, useRef } from 'react';
import { Camera, Loader } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { uploadFile } from '../../services/storage/uploadService';

interface ImageUploadProps {
  onUpload: (url: string) => void;
  currentImageUrl?: string;
  className?: string;
}

export function ImageUpload({ onUpload, currentImageUrl, className = '' }: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentImageUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null); 

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset error state
    setError(null);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      toast.error('Please upload an image file');
      e.target.value = '';
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      toast.error('Image must be less than 5MB');
      e.target.value = '';
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    // Create a sanitized file name
    const fileExt = file.name.split('.').pop();
    const sanitizedName = `image-${Date.now()}.${fileExt}`;
    const sanitizedFile = new File([file], sanitizedName, { type: file.type });

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    setIsUploading(true);
    setError(null);

    try {
      const { url } = await uploadFile(sanitizedFile, 'images', `uploads/${sanitizedName}`);
      setPreview(url);
      onUpload(url);
      toast.success('Image uploaded successfully');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to upload image';
      console.error('Error uploading image:', message);
      setError(message);
      toast.error(message);
      setPreview(currentImageUrl || null);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {preview ? (
        <div className="relative aspect-video rounded-xl overflow-hidden">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={() => {
              setPreview(null);
              setError('Failed to load image');
            }}
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className={`px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors ${
                isUploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isUploading ? (
                <Loader className="h-5 w-5 animate-spin" />
              ) : (
                'Change Image'
              )}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="w-full aspect-video border-2 border-dashed border-gray-700 rounded-xl flex flex-col items-center justify-center hover:border-accent-green transition-colors"
        >
          {isUploading ? (
            <Loader className="h-8 w-8 text-accent-green animate-spin" />
          ) : (
            <>
              <Camera className="h-8 w-8 text-gray-400 mb-2" />
              <span className="text-sm text-gray-400">Click to upload image</span>
            </>
          )}
        </button>
      )}
      
      {error && (
        <p className="mt-2 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}